import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { Plus, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import ExportMenu from "@/components/ExportMenu";

const inr = (n) =>
  new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 })
    .format(n || 0);

export default function Ledger() {
  const { user } = useAuth();
  const [fy, setFy] = useState("");
  const [currentFy, setCurrentFy] = useState("");
  const [summary, setSummary] = useState([]);
  const [clientId, setClientId] = useState("");
  const [branchId, setBranchId] = useState("");
  const [month, setMonth] = useState("");
  const [branches, setBranches] = useState([]);
  const [detail, setDetail] = useState(null);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openEntry, setOpenEntry] = useState(false);
  const canEdit = ["crm_manager", "accounts"].includes(user?.role);

  useEffect(() => {
    (async () => {
      const { data } = await api.get("/ledger/fy/current");
      setCurrentFy(data.current_fy);
      setFy(data.current_fy);
      const c = await api.get("/clients", { params: { page_size: 200 } });
      setClients(c.data.items);
      const br = await api.get("/branches");
      setBranches(br.data);
    })();
  }, []);

  const loadSummary = async () => {
    if (!fy) return;
    setLoading(true);
    try {
      const { data } = await api.get("/ledger/summary", { params: { fy } });
      setSummary(data.rows);
    } finally { setLoading(false); }
  };

  const loadDetail = async () => {
    if (!clientId || !fy) { setDetail(null); return; }
    const { data } = await api.get(`/ledger/client/${clientId}`, { params: { fy } });
    setDetail(data);
  };

  useEffect(() => { loadSummary(); /* eslint-disable-next-line */ }, [fy]);
  useEffect(() => { loadDetail(); /* eslint-disable-next-line */ }, [clientId, fy]);

  // FY options — last 3 + current + next
  const fyOptions = (() => {
    if (!currentFy) return [];
    const year = parseInt(currentFy.slice(2, 6), 10);
    const out = [];
    for (let y = year - 3; y <= year + 1; y++) {
      out.push(`FY${y}-${String((y + 1) % 100).padStart(2, "0")}`);
    }
    return out;
  })();

  const totals = summary.reduce((acc, r) => ({
    opening: acc.opening + r.opening,
    debits: acc.debits + r.debits,
    credits: acc.credits + r.credits,
    closing: acc.closing + r.closing,
  }), { opening: 0, debits: 0, credits: 0, closing: 0 });

  return (
    <div className="p-6 md:p-8 space-y-4" data-testid="ledger-page">
      <div className="flex justify-between items-end flex-wrap gap-3">
        <div>
          <div className="mptg-overline">Finance · ledger</div>
          <h1 className="font-heading text-3xl font-bold tracking-tight mt-1">Client Ledger</h1>
          <p className="text-slate-500 mt-1">Financial year opening / debits / credits / closing per client.</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Label className="text-xs uppercase tracking-wider text-slate-500">Financial year</Label>
          <Select value={fy} onValueChange={setFy}>
            <SelectTrigger className="w-44" data-testid="fy-select"><SelectValue placeholder="FY" /></SelectTrigger>
            <SelectContent>
              {fyOptions.map((f) => <SelectItem key={f} value={f}>{f}{f === currentFy ? " (current)" : ""}</SelectItem>)}
            </SelectContent>
          </Select>
          <ExportMenu
            endpoint="/ledger/export"
            params={{ fy, client_id: clientId, branch_id: branchId, month, mode: "detail" }}
            filename={`ledger_${fy || "current"}`}
            label="Detail"
            testid="ledger-export"
          />
          <ExportMenu
            endpoint="/ledger/export"
            params={{ fy, client_id: clientId, branch_id: branchId, mode: "summary" }}
            filename={`ledger_summary_${fy || "current"}`}
            label="Summary"
            testid="ledger-summary-export"
          />
          <ExportMenu
            endpoint="/ledger/export"
            params={{ fy, client_id: clientId, branch_id: branchId, month, mode: "payments" }}
            filename={`ledger_payments_${fy || "current"}`}
            label="Payments"
            testid="ledger-payments-export"
          />
          {canEdit && (
            <Dialog open={openEntry} onOpenChange={setOpenEntry}>
              <DialogTrigger asChild>
                <Button variant="outline" className="border-slate-200" data-testid="new-entry-btn">
                  <Plus className="w-4 h-4 mr-1" /> New entry
                </Button>
              </DialogTrigger>
              <NewEntryDialog clients={clients} onDone={() => { setOpenEntry(false); loadSummary(); loadDetail(); }} />
            </Dialog>
          )}
        </div>
      </div>

      {/* Filters */}
      <div className="mptg-card flex flex-wrap items-center gap-3" data-testid="ledger-filters">
        <Select value={clientId || "all"} onValueChange={(v) => setClientId(v === "all" ? "" : v)}>
          <SelectTrigger className="w-64" data-testid="ledger-client-filter"><SelectValue placeholder="All clients" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All clients</SelectItem>
            {clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={branchId || "all"} onValueChange={(v) => setBranchId(v === "all" ? "" : v)}>
          <SelectTrigger className="w-56" data-testid="ledger-branch-filter"><SelectValue placeholder="All branches" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All branches</SelectItem>
            {branches.map((b) => <SelectItem key={b.id} value={b.id}>{b.code} · {b.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Input type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="w-40" data-testid="ledger-month-filter" />
        {(clientId || branchId || month) && (
          <button
            onClick={() => { setClientId(""); setBranchId(""); setMonth(""); }}
            className="text-xs text-[#2E86C1] hover:underline"
            data-testid="ledger-clear-filters"
          >
            Clear
          </button>
        )}
        <span className="text-xs text-slate-400 ml-auto">
          Tip: Export respects these filters. For a client T-account, pick the client below.
        </span>
      </div>

      {/* Portfolio ledger totals */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card label="Opening (portfolio)" value={inr(totals.opening)} />
        <Card label="Debits (invoices + adj)" value={inr(totals.debits)} tone="accent" />
        <Card label="Credits (receipts)" value={inr(totals.credits)} tone="success" />
        <Card label="Closing outstanding" value={inr(totals.closing)} tone={totals.closing > 0 ? "warning" : "success"} />
      </div>

      {/* Per-client summary */}
      <div className="mptg-card p-0 overflow-hidden">
        <div className="p-4 flex items-center justify-between">
          <div className="mptg-overline">Per-client summary · {fy}</div>
          <Select value={clientId || "none"} onValueChange={(v) => setClientId(v === "none" ? "" : v)}>
            <SelectTrigger className="w-64" data-testid="ledger-client-select">
              <SelectValue placeholder="Open a client ledger…" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">— pick client —</SelectItem>
              {clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <table className="w-full mptg-table">
          <thead><tr><th>Client</th><th className="text-right">Opening</th><th className="text-right">Debits</th><th className="text-right">Credits</th><th className="text-right">Closing</th><th>Entries</th><th></th></tr></thead>
          <tbody>
            {loading ? <tr><td colSpan={7} className="py-8 text-center text-slate-400">Loading…</td></tr> :
             summary.length === 0 ? <tr><td colSpan={7} className="py-8 text-center text-slate-400">No ledger entries in {fy}.</td></tr> :
              summary.map((r) => (
                <tr key={r.client_id} onClick={() => setClientId(r.client_id)} className="cursor-pointer">
                  <td>
                    <Link to={`/clients/${r.client_id}`} className="font-semibold hover:text-[#2E86C1]">{r.client_name}</Link>
                  </td>
                  <td className="text-right tabular-nums">{inr(r.opening)}</td>
                  <td className="text-right tabular-nums text-[#2E86C1]">{inr(r.debits)}</td>
                  <td className="text-right tabular-nums text-[#1E8449]">{inr(r.credits)}</td>
                  <td className={`text-right tabular-nums font-semibold ${r.closing > 0 ? "text-[#F39C12]" : "text-[#1E8449]"}`}>{inr(r.closing)}</td>
                  <td className="tabular-nums text-xs text-slate-500">{r.entries}</td>
                  <td className="text-right"><ArrowRight className="w-3.5 h-3.5 text-slate-400 inline" /></td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>

      {/* Detail */}
      {detail && (
        <div className="mptg-card p-0 overflow-hidden" data-testid="ledger-detail">
          <div className="p-4 flex items-center justify-between flex-wrap gap-2">
            <div>
              <div className="mptg-overline">T-account · {detail.fy} ({detail.fy_start} → {detail.fy_end})</div>
              <div className="font-heading text-xl font-bold">{detail.client.name}</div>
            </div>
            <div className="flex items-center gap-3 text-sm">
              <Metric label="Opening" value={inr(detail.opening_balance)} />
              <Metric label="Debits" value={inr(detail.totals.debits)} />
              <Metric label="Credits" value={inr(detail.totals.credits)} />
              <Metric label="Closing" value={inr(detail.closing_balance)} tone={detail.closing_balance > 0 ? "warning" : "success"} />
            </div>
          </div>
          <table className="w-full mptg-table">
            <thead><tr><th>Date</th><th>Type</th><th>Reference / Notes</th><th className="text-right">Debit</th><th className="text-right">Credit</th><th className="text-right">Balance</th></tr></thead>
            <tbody>
              {detail.entries.map((e) => (
                <tr key={e.id}>
                  <td className="text-xs font-mono">{e.entry_date}</td>
                  <td>
                    <span className={`rounded px-1.5 py-0.5 text-[11px] font-semibold uppercase ${
                      e.entry_type === "invoice" ? "badge-blue" :
                      e.entry_type === "receipt" ? "badge-green" :
                      e.entry_type === "opening" ? "badge-slate" :
                      e.entry_type === "adjustment" ? "badge-amber" :
                      "badge-slate"
                    }`}>{e.entry_type.replace("_", " ")}</span>
                  </td>
                  <td className="text-xs text-slate-700">
                    <div className="font-medium">{e.reference || e.invoice_number || "-"}</div>
                    {e.notes && <div className="text-slate-500">{e.notes}</div>}
                  </td>
                  <td className="text-right tabular-nums">{e.debit > 0 ? inr(e.debit) : ""}</td>
                  <td className="text-right tabular-nums">{e.credit > 0 ? inr(e.credit) : ""}</td>
                  <td className="text-right tabular-nums font-semibold">{inr(e.balance)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function Card({ label, value, tone }) {
  const tones = { success: "text-[#1E8449]", warning: "text-[#F39C12]", danger: "text-[#C0392B]", accent: "text-[#2E86C1]" };
  return (
    <div className="mptg-card">
      <div className="mptg-overline">{label}</div>
      <div className={`font-heading text-2xl font-bold mt-1 ${tones[tone] || "text-slate-800"}`}>{value}</div>
    </div>
  );
}
function Metric({ label, value, tone }) {
  const tones = { success: "text-[#1E8449]", warning: "text-[#F39C12]" };
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-slate-500">{label}</div>
      <div className={`font-heading font-bold ${tones[tone] || "text-slate-800"}`}>{value}</div>
    </div>
  );
}

function NewEntryDialog({ clients, onDone }) {
  const [f, setF] = useState({
    client_id: "", entry_date: new Date().toISOString().slice(0, 10),
    entry_type: "adjustment", amount: 0, reference: "", invoice_number: "", notes: "",
  });
  const [saving, setSaving] = useState(false);
  const save = async () => {
    if (!f.client_id) { toast.error("Pick a client"); return; }
    if (!f.amount) { toast.error("Amount must be non-zero"); return; }
    setSaving(true);
    try {
      await api.post("/ledger/entry", f);
      toast.success("Entry added"); onDone();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  return (
    <DialogContent className="max-w-lg">
      <DialogHeader><DialogTitle>New ledger entry</DialogTitle></DialogHeader>
      <div className="space-y-3 py-2">
        <div><Label>Client *</Label>
          <Select value={f.client_id} onValueChange={(v) => setF({ ...f, client_id: v })}>
            <SelectTrigger><SelectValue placeholder="Pick client" /></SelectTrigger>
            <SelectContent>{clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div><Label>Entry type</Label>
            <Select value={f.entry_type} onValueChange={(v) => setF({ ...f, entry_type: v })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="opening">Opening balance</SelectItem>
                <SelectItem value="invoice">Invoice (debit)</SelectItem>
                <SelectItem value="receipt">Receipt (credit)</SelectItem>
                <SelectItem value="credit_note">Credit note (credit)</SelectItem>
                <SelectItem value="debit_note">Debit note (debit)</SelectItem>
                <SelectItem value="adjustment">Adjustment (debit)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div><Label>Amount (₹)</Label>
            <Input type="number" value={f.amount} onChange={(e) => setF({ ...f, amount: +e.target.value })} /></div>
          <div><Label>Entry date</Label>
            <Input type="date" value={f.entry_date} onChange={(e) => setF({ ...f, entry_date: e.target.value })} /></div>
          <div><Label>Reference</Label>
            <Input value={f.reference} onChange={(e) => setF({ ...f, reference: e.target.value })} placeholder="UTR / voucher" /></div>
        </div>
        <div><Label>Notes</Label>
          <Textarea rows={2} value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} /></div>
      </div>
      <DialogFooter>
        <Button onClick={save} disabled={saving} className="bg-[#2E86C1] hover:bg-[#2471A3] text-white" data-testid="save-entry-btn">Save</Button>
      </DialogFooter>
    </DialogContent>
  );
}
